import React from 'react';
import { Box, Stack, Typography, Divider, Link, Button, Fade } from '@mui/material';
import UserVerification from '../UserVerification';
import Action from '../Action';
import BrandBanner, { hasBrandBanner } from './shared/BrandBanner';
import PoweredBy from './shared/PoweredBy';
import SocialRow from './shared/SocialRow';
import CopyIconButton from './shared/CopyIconButton';
import PolicyLinks from './shared/PolicyLinks';
import TrackAnotherButton from './shared/TrackAnotherButton';
import {
    getReadableAccent, getProgress, STEPS, formatDate, formatDateTime, formatINR, sortedHistory, toAbsoluteUrl,
} from './trackingTemplateUtils';

const CANVAS = '#FCFCFB';
const INK = '#1C1C1A';
const MUTE = '#9A9A95';
const HAIR = '#ECECEA';
const HAIR2 = '#DcDcDa';
const GREEN = '#4E7A5E';
const CLAY = '#B4685A';

// Slim horizontal progress rail — 7px node dots on a 1px rule.
function ProgressRail({ activeIdx, complete, color }) {
    const ratio = complete ? 1 : (STEPS.length > 1 ? activeIdx / (STEPS.length - 1) : 0);
    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            <Box sx={{ position: 'absolute', top: 3, left: '12.5%', right: '12.5%', height: '1px', bgcolor: HAIR2 }} />
            <Box sx={{ position: 'absolute', top: 3, left: '12.5%', width: `calc(75% * ${ratio})`, height: '1px', bgcolor: color, transition: 'width .5s ease' }} />
            <Stack direction="row" sx={{ position: 'relative' }}>
                {STEPS.map((label, i) => {
                    const on = complete || i <= activeIdx;
                    return (
                        <Stack key={label} alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: on ? color : HAIR2, zIndex: 1 }} />
                            <Typography sx={{
                                fontSize: { xs: 9, sm: 10 }, letterSpacing: '0.5px', textTransform: 'uppercase',
                                textAlign: 'center', color: on ? INK : MUTE, fontWeight: 500, lineHeight: 1.3,
                                display: { xs: i === activeIdx || i === 0 || i === STEPS.length - 1 ? 'block' : 'none', sm: 'block' },
                            }}>
                                {label}
                            </Typography>
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );
}

function MetaRow({ label, value, copyable }) {
    if (!value) return null;
    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            sx={{ py: 1.25, borderBottom: { xs: `1px solid ${HAIR}`, sm: 'none' } }}
            spacing={{ xs: 0.25, sm: 2 }}
        >
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: MUTE }}>{label}</Typography>
            {copyable ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ justifyContent: { sm: 'flex-end' } }}>
                    <Typography sx={{ fontSize: 15, color: INK, letterSpacing: '0.5px' }}>{value}</Typography>
                    <CopyIconButton value={value} color={MUTE} size={13} />
                </Stack>
            ) : (
                <Typography sx={{ fontSize: 15, color: INK, textAlign: { sm: 'right' } }}>{value}</Typography>
            )}
        </Stack>
    );
}

// Custom slim vertical timeline (hairline aesthetic — intentionally not the chunky shared Stepper).
function SlimTimeline({ history, accent }) {
    return (
        <Stack>
            {history.map((item, i) => {
                const last = i === history.length - 1;
                const newest = i === 0;
                return (
                    <Stack key={i} direction="row" spacing={2}>
                        <Stack alignItems="center" sx={{ width: 8 }}>
                            <Box sx={{
                                width: 7, height: 7, borderRadius: '50%', mt: '6px',
                                bgcolor: newest ? accent : 'transparent',
                                border: newest ? 'none' : `1px solid ${HAIR2}`,
                            }} />
                            {!last && <Box sx={{ flexGrow: 1, width: '1px', bgcolor: HAIR, my: 0.5 }} />}
                        </Stack>
                        <Box sx={{ pb: last ? 0 : 2.5, flex: 1 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: INK }}>{item.orderStatusDescription}</Typography>
                            {item.currentLocation && <Typography sx={{ fontSize: 12, color: MUTE }}>{item.currentLocation}</Typography>}
                            {item.remark && <Typography sx={{ fontSize: 12, color: MUTE }}>{item.remark}</Typography>}
                            <Typography sx={{ fontSize: 11, color: MUTE, mt: 0.25 }}>{formatDateTime(item.timestamp)}</Typography>
                        </Box>
                    </Stack>
                );
            })}
        </Stack>
    );
}

function statusSentence(progress) {
    switch (progress.phase) {
        case 'DELIVERED': return 'Delivered.';
        case 'FAILED': return 'Delivery attempt failed.';
        case 'RTO': return 'On its way back.';
        case 'CANCELLED': return 'Order cancelled.';
        case 'LOST': return 'Shipment delayed.';
        default:
            if (progress.bucket === 'Out for delivery') return 'Out for delivery.';
            if (progress.bucket === 'Shipment In Transit') return 'In transit.';
            return 'Order placed.';
    }
}

export default function Minimal({ brand, order, preview, children }) {
    const data = order || {};
    const hasOrder = !!data.createdDate;
    const pal = getReadableAccent(brand && brand.bannerBgColor);
    const progress = getProgress(data.currentStatus);
    // Newest-first, like the legacy timeline — the payload's raw order is not guaranteed.
    const history = sortedHistory(data);
    const [showAll, setShowAll] = React.useState(false);
    const shown = showAll ? history : history.slice(0, 6);

    const tone = progress.phase === 'DELIVERED' ? GREEN
        : (progress.phase === 'FAILED' || progress.phase === 'RTO' || progress.phase === 'LOST') ? CLAY
            : progress.phase === 'CANCELLED' ? MUTE
                : pal.accentText;

    const logo = brand && brand.logo;
    const name = (brand && brand.name) || 'Your Brand';
    const webUrl = toAbsoluteUrl(brand && brand.webUrl);
    const showBanner = hasBrandBanner(brand);

    // Null-safe: bucket is undefined when there's no order (search/not-found states); this initial
    // value is always overwritten by the phase branches below — it just must not crash.
    let eyebrow = (progress.bucket || '').toUpperCase();
    let subLine = '';
    if (progress.phase === 'DELIVERED') {
        eyebrow = 'DELIVERED';
        const t = (history[0] && history[0].timestamp) || data.edd;
        subLine = formatDateTime(t) ? `Delivered ${formatDateTime(t)}` : '';
    } else if (progress.phase === 'NORMAL') {
        eyebrow = data.edd ? 'ARRIVING' : 'ON ITS WAY';
        subLine = formatDate(data.edd) ? `Expected by ${formatDate(data.edd)}` : 'On its way to you';
    } else if (progress.phase === 'FAILED') {
        eyebrow = 'ACTION NEEDED'; subLine = "We're rearranging your delivery.";
    } else if (progress.phase === 'RTO') {
        eyebrow = 'RETURNING'; subLine = 'This parcel is on its way back to the sender.';
    } else if (progress.phase === 'CANCELLED') {
        eyebrow = 'CANCELLED'; subLine = '';
    } else if (progress.phase === 'LOST') {
        eyebrow = 'DELAYED'; subLine = 'This shipment is under investigation.';
    }

    const showRail = progress.phase !== 'CANCELLED' && progress.phase !== 'LOST';

    return (
        <Box sx={{ bgcolor: CANVAS, minHeight: '100vh' }}>
            {/* Full-bleed brand banner (optional), shown above the editorial column */}
            {showBanner && <BrandBanner brand={brand} />}
            <Fade in timeout={450}>
                <Box sx={{ maxWidth: 560, mx: 'auto', px: { xs: 3, sm: 2 }, pt: showBanner ? { xs: 4, sm: 6 } : { xs: 6, sm: 9 }, pb: 8 }}>
                    {/* Header — centered logo, no bar (hidden when a banner is set) */}
                    {!showBanner && (
                        <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
                            {logo ? (
                                <Link href={webUrl || undefined} target="_blank" rel="noreferrer" underline="none">
                                    <Box component="img" src={logo} alt={name} sx={{ height: 44, maxWidth: { xs: 220, sm: 320 }, objectFit: 'contain' }} />
                                </Link>
                            ) : (
                                <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: INK }}>{name}</Typography>
                            )}
                        </Box>
                    )}

                    {/* OTP verification gate — same shared component as the current page (renders only for NDR) */}
                    <Box sx={{ mb: 4, '&:empty': { display: 'none' } }}><UserVerification /></Box>

                    {/* Same "← Track another order" affordance as the current page (hidden in preview) */}
                    {hasOrder && <Box sx={{ mb: 3 }}><TrackAnotherButton preview={preview} /></Box>}

                    {/* Center swaps by state: tracking body when an order is loaded, else the search
                        box / not-found passed as children — header/footer stay constant. */}
                    {hasOrder ? (
                        <>
                    {/* Status hero — text only */}
                    <Box sx={{ textAlign: 'center', mb: { xs: 5, sm: 7 } }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: MUTE }}>{eyebrow}</Typography>
                        </Stack>
                        <Typography sx={{ fontWeight: 300, color: INK, lineHeight: 1.2, fontSize: { xs: 26, sm: 34 }, letterSpacing: '-0.3px' }}>
                            {statusSentence(progress)}
                        </Typography>
                        {subLine && <Typography sx={{ fontSize: 15, color: MUTE, mt: 1.5 }}>{subLine}</Typography>}
                        {/* Cancel / Re-attempt — same shared component as the current page (renders only for NDR + OTP verified) */}
                        <Box sx={{ mt: 3, justifyContent: 'center', display: 'flex', '&:empty': { display: 'none' } }}><Action /></Box>
                    </Box>

                    {/* Slim progress rail */}
                    {showRail && (
                        <Box sx={{ mb: { xs: 5, sm: 7 } }}>
                            <ProgressRail activeIdx={Math.min(progress.step, 3)} complete={progress.phase === 'DELIVERED'} color={tone} />
                        </Box>
                    )}

                    <Divider sx={{ borderColor: HAIR }} />

                    {/* Order meta — definition list */}
                    <Box sx={{ py: { xs: 1, sm: 2 } }}>
                        <MetaRow label="Order" value={(data.shippingLabelId || '').toUpperCase()} />
                        <MetaRow label="AWB" value={(data.waybill || '').toUpperCase()} copyable />
                        <MetaRow label="Courier" value={(data.courierPartnerParent || '').toUpperCase()} />
                        <MetaRow label="Payment" value={data.paymentMode ? (data.paymentMode.toUpperCase() === 'COD' ? 'Cash on delivery' : data.paymentMode.toUpperCase() === 'PREPAID' ? 'Prepaid' : data.paymentMode) : ''} />
                        <MetaRow label="From" value={(data.pickupLocation || '').toUpperCase()} />
                        <MetaRow label="To" value={(data.deliveryLocation || '').toUpperCase()} />
                        <MetaRow label="Order value" value={formatINR(data.invoiceValue)} />
                    </Box>

                    {history.length > 0 && (
                        <>
                            <Divider sx={{ borderColor: HAIR }} />
                            <Box sx={{ pt: { xs: 3, sm: 4 } }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: MUTE, mb: 3 }}>Journey</Typography>
                                <SlimTimeline history={shown} accent={tone} />
                                {history.length > 6 && (
                                    <Button onClick={() => setShowAll((s) => !s)} size="small"
                                        sx={{ mt: 2, textTransform: 'none', color: pal.accentStrong, fontWeight: 500, px: 0 }}>
                                        {showAll ? 'Show less' : 'Show full journey'}
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                        </>
                    ) : children}

                    {/* Footer */}
                    <Box sx={{ mt: { xs: 8, sm: 10 } }}>
                        <Divider sx={{ borderColor: HAIR, mb: 4 }} />
                        <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
                            {brand && brand.footer && (
                                <Typography sx={{ fontSize: 13, color: MUTE, maxWidth: 420, whiteSpace: 'pre-wrap' }}>{brand.footer}</Typography>
                            )}
                            <SocialRow brand={brand} color={MUTE} hoverColor={pal.accentText} size={18} spacing={1.5} />
                            <PolicyLinks brand={brand} color={MUTE} size={11} />
                            <PoweredBy logoHeight={16} labelSx={{ fontSize: 10, letterSpacing: '1px', color: MUTE, textTransform: 'uppercase' }} />
                        </Stack>
                    </Box>
                </Box>
            </Fade>
        </Box>
    );
}
